import { VisibilityOutlined } from "@mui/icons-material";
import { Divider, FormControlLabel, Link, Stack, Switch, Typography } from "@mui/material";
import type { ChangeEvent } from "react";
import { FormRecipientType, FormSubmissionPrivacyType, Links } from "src/constants";
import { useSession } from "src/hooks";
import { useEditFormSubmissionPrivacySetting, type FormSubmissionDetailDto } from "src/http";
import { ItemProperty } from "../itemProperties";

interface Props {
  formSubmission: FormSubmissionDetailDto;
}

const FormSubmissionVisibilityProperty = (props: Props) => {
  const { userId: sessionUserId } = useSession();
  const { mutate, isLoading } = useEditFormSubmissionPrivacySetting();

  const isRecipient = props.formSubmission.recipientUser.userId === sessionUserId;
  const showRecipient = props.formSubmission.recipientType !== FormRecipientType.Self;
  const showCreator = props.formSubmission.privacySetting !== FormSubmissionPrivacyType.Public;
  const showPeopleLead = props.formSubmission.privacySetting === FormSubmissionPrivacyType.RecipientAndPeopleLead;
  const showPrivilegedUsers = props.formSubmission.privacySetting !== FormSubmissionPrivacyType.Public;

  function handleVisibilityChanged(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    mutate({
      formSubmissionGuid: props.formSubmission.formSubmissionGuid,
      privacySetting: checked ? FormSubmissionPrivacyType.Public : FormSubmissionPrivacyType.RecipientOnly,
    });
  }

  // If an item is a draft, it's only visible to its creator.
  if (props.formSubmission.submittedDateTimeUtc == null) {
    return (
      <ItemProperty labelIcon={<VisibilityOutlined />} label="Visibility">
        <Link href={Links.User(props.formSubmission.createdByUser.userId)} sx={{ whiteSpace: "nowrap" }}>
          {props.formSubmission.createdByUser.displayName}
        </Link>
      </ItemProperty>
    );
  }

  return (
    <ItemProperty labelIcon={<VisibilityOutlined />} label="Visibility">
      <Stack spacing={0.5} sx={{ alignItems: "start" }}>
        {props.formSubmission.privacySetting === FormSubmissionPrivacyType.Public && (
          <Typography sx={{ textWrap: "nowrap" }}>Public to Organization</Typography>
        )}

        {showCreator && (
          <Link href={Links.User(props.formSubmission.createdByUser.userId)} sx={{ whiteSpace: "nowrap" }}>
            {props.formSubmission.createdByUser.displayName}
          </Link>
        )}

        {showRecipient && (
          <Link href={Links.User(props.formSubmission.recipientUser.userId)} sx={{ whiteSpace: "nowrap" }}>
            {props.formSubmission.recipientUser.displayName}
          </Link>
        )}

        {showPeopleLead && props.formSubmission.recipientUserPeopleLead && (
          <Typography>
            People Lead{" "}
            <Link href={Links.User(props.formSubmission.recipientUserPeopleLead.userId)} sx={{ whiteSpace: "nowrap" }}>
              {props.formSubmission.recipientUserPeopleLead.displayName}
            </Link>
          </Typography>
        )}

        {showPrivilegedUsers && props.formSubmission.priviledgedUsers.length > 0 && (
          <Typography>
            {props.formSubmission.priviledgedUsersLabel}{" "}
            {props.formSubmission.priviledgedUsers
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((user, index, array) => (
                <span key={index}>
                  <Link href={Links.User(user.userId)} sx={{ whiteSpace: "nowrap" }}>
                    {user.displayName}
                  </Link>
                  {index < array.length - 1 && ", "}
                </span>
              ))}
          </Typography>
        )}

        {props.formSubmission.recipientCanMakePublic && isRecipient && (
          <Stack spacing={0.5} sx={{ alignItems: "start", width: "100%" }}>
            <Divider variant="dashed" flexItem />
            <FormControlLabel
              control={
                <Switch
                  disabled={isLoading}
                  onChange={handleVisibilityChanged}
                  checked={props.formSubmission.privacySetting === FormSubmissionPrivacyType.Public}
                  sx={{ mr: 0.5 }}
                />
              }
              label="Visible to All"
            />
          </Stack>
        )}
      </Stack>
    </ItemProperty>
  );
};

export { FormSubmissionVisibilityProperty };
